/**
 * AttesterReplacement.js
 *
 * WHAT: Permanent attester replacement for active attestation campaigns.
 * WHY:  When an attester leaves the organization or changes roles during an active campaign,
 *       their pending attestations must be reassigned to someone else. Unlike delegation
 *       (temporary), replacement permanently transfers all of an attester's pending reviews
 *       to a new person, maintaining accountability and ensuring the campaign can complete.
 */
import React, { useState, useEffect, useCallback } from 'react';
import API_CONFIG from '../config/api.config';
import { useSafeAsync, useSearchFilter } from '../hooks';
import { WideModal } from './common';

/**
 * WHAT: Attester replacement dashboard showing pending attesters and replacement controls.
 * WHY:  Provides admins with a campaign-scoped view of all attesters who have pending
 *       reviews, letting them select a replacement attester and transfer all pending
 *       records in a single action to keep the campaign on track.
 */
const AttesterReplacement = () => {
  // Use useSafeAsync for campaigns data
  const {
    data: campaignsData,
    execute: loadCampaigns
  } = useSafeAsync(
    async () => {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('campaigns/'));
      const data = await response.json();
      if (data.success) {
        // Filter only ATTESTATION status campaigns
        return data.campaigns.filter(c => c.status === 'ATTESTATION');
      }
      return [];
    },
    [],
    { immediate: true }
  );

  // Use useSafeAsync for enterprise users
  const {
    data: enterpriseUsers,
    execute: loadEnterpriseUsers
  } = useSafeAsync(
    async () => {
      const response = await fetch(API_CONFIG.getEnterpriseUsersEndpoint(''));
      const data = await response.json();
      return data.success ? data.users : [];
    },
    [],
    { immediate: true }
  );

  // Use useSafeAsync for pending attesters
  const {
    data: pendingAttesters,
    execute: loadPendingAttesters
  } = useSafeAsync(
    async () => {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attester-replacements/pending-attesters/'));
      const data = await response.json();
      return data.success ? data.attesters : [];
    },
    [],
    { immediate: true }
  );

  // Local state for replacements (depends on selected campaign)
  const [replacements, setReplacements] = useState([]);
  const [isLoadingReplacements, setIsLoadingReplacements] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // UI state
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  // Replacement result shown in a WideModal after Apply Replacement succeeds.
  // Holds {total_records_updated, campaigns_updated:[{campaign_id, campaign_name, records_updated}], old_attester, new_attester}
  const [replacementResult, setReplacementResult] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [activeTab, setActiveTab] = useState('action');

  // Refresh all data
  const handleRefresh = async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      await Promise.all([
        loadCampaigns(),
        loadEnterpriseUsers(),
        loadPendingAttesters()
      ]);
      if (selectedCampaignId) {
        await loadReplacements(selectedCampaignId);
      }
      setSuccessMessage('Data refreshed successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Error refreshing data: ' + err.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Use useSearchFilter for replacements search
  const {
    searchTerm,
    setSearchTerm,
    filterBySearch
  } = useSearchFilter('', {
    searchFields: ['database_name', 'db_username', 'from_attester_email', 'to_attester_email', 'replaced_by']
  });

  // Attester-first workflow state
  const [selectedAttester, setSelectedAttester] = useState(null);
  const [attesterDetails, setAttesterDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [suggestedReplacements, setSuggestedReplacements] = useState({ manager: null, colleagues: [] });

  // Form state for create
  const [formData, setFormData] = useState({
    old_attester_email: '',
    new_attester_email: '',
    new_attester_name: '',
    reason: '',
    comments: ''
  });

  // Reason codes
  const reasonCodes = [
    { value: 'OUT_OF_OFFICE', label: 'Out of Office' },
    { value: 'TERMINATED', label: 'Terminated' },
    { value: 'RESIGNED', label: 'Resigned' },
    { value: 'TEAM_CHANGE', label: 'Team Change' },
    { value: 'RESTRUCTURE', label: 'Restructure' },
    { value: 'WORKLOAD', label: 'Workload Balancing' },
    { value: 'OTHER', label: 'Other' }
  ];

  // Derived data from hooks
  const campaigns = campaignsData || [];
  const attesters = pendingAttesters || [];
  const users = enterpriseUsers || [];

  // Load replacements when campaign changes
  const loadReplacements = useCallback(async (campaignId) => {
    if (!campaignId) {
      setReplacements([]);
      return;
    }

    setIsLoadingReplacements(true);
    setError(null);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attester-replacements/?campaign_id=${campaignId}`));
      const data = await response.json();

      if (data.success) {
        setReplacements(data.replacements);
      } else {
        setError('Failed to load replacements');
      }
    } catch (err) {
      setError('Error loading replacements: ' + err.message);
    } finally {
      setIsLoadingReplacements(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCampaignId) {
      loadReplacements(selectedCampaignId);
    }
  }, [selectedCampaignId, loadReplacements]);

  // Load attester details when attester is selected
  const loadAttesterDetails = useCallback(async (attesterEmail) => {
    setIsLoadingDetails(true);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`attester-replacements/attester-details/${encodeURIComponent(attesterEmail)}/`));
      const data = await response.json();

      if (data.success) {
        setAttesterDetails(data);
        // Auto-select all campaigns by default
        setSelectedCampaignIds(data.campaigns.map(c => c.campaign_id));
        // Set suggested replacements
        setSuggestedReplacements(data.suggested_replacements || { manager: null, colleagues: [] });
      } else {
        setAttesterDetails(null);
        setSuggestedReplacements({ manager: null, colleagues: [] });
        setError('Failed to load attester details');
      }
    } catch (err) {
      console.error('Error loading attester details:', err.message);
      setAttesterDetails(null);
      setSuggestedReplacements({ manager: null, colleagues: [] });
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  useEffect(() => {
    if (selectedAttester) {
      loadAttesterDetails(selectedAttester.email);
    } else {
      setAttesterDetails(null);
      setSelectedCampaignIds([]);
      setSuggestedReplacements({ manager: null, colleagues: [] });
    }
  }, [selectedAttester, loadAttesterDetails]);

  const handleCreateReplacement = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!formData.old_attester_email || !formData.new_attester_email) {
      setError('Please fill in all required fields');
      return;
    }

    if (selectedCampaignIds.length === 0) {
      setError('Please select at least one campaign');
      return;
    }

    if (formData.old_attester_email.toLowerCase() === formData.new_attester_email.toLowerCase()) {
      setError('Old attester and new attester cannot be the same');
      return;
    }

    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attester-replacements/create/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          old_attester_email: formData.old_attester_email,
          new_attester_email: formData.new_attester_email,
          new_attester_name: formData.new_attester_name,
          campaign_ids: selectedCampaignIds,
          reason: formData.reason,
          comments: formData.comments,
          replaced_by: 'ADMIN'
        }),
      });

      const data = await response.json();

      if (data.success) {
        setReplacementResult(data);
        setShowCreateModal(false);
        resetForm();
        loadPendingAttesters();
        // Refresh the stat card. If no campaign is currently selected in the
        // dropdown, auto-select the first affected campaign so the updated
        // "Attester Replaced Records" count is visible without a manual switch.
        const affectedIds = (data.campaigns_updated || []).map(c => c.campaign_id);
        const campaignToShow = selectedCampaignId || affectedIds[0] || '';
        if (campaignToShow && campaignToShow !== selectedCampaignId) {
          setSelectedCampaignId(campaignToShow);
        } else if (campaignToShow) {
          loadReplacements(campaignToShow);
        }
      } else {
        setError(data.error || 'Failed to create replacement');
      }
    } catch (err) {
      setError('Error creating replacement: ' + err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      old_attester_email: '',
      new_attester_email: '',
      new_attester_name: '',
      reason: '',
      comments: ''
    });
    setSelectedAttester(null);
    setAttesterDetails(null);
    setSelectedCampaignIds([]);
    setSuggestedReplacements({ manager: null, colleagues: [] });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const handleAttesterSelect = (attester) => {
    setSelectedAttester(attester);
    setFormData(prev => ({
      ...prev,
      old_attester_email: attester.email,
      // Auto-suggest reason based on account status
      reason: attester.suggested_reason || prev.reason
    }));
  };

  const handleCampaignToggle = (campaignId) => {
    setSelectedCampaignIds(prev => {
      if (prev.includes(campaignId)) {
        return prev.filter(id => id !== campaignId);
      } else {
        return [...prev, campaignId];
      }
    });
  };

  const handleSelectAllCampaigns = () => {
    if (attesterDetails) {
      setSelectedCampaignIds(attesterDetails.campaigns.map(c => c.campaign_id));
    }
  };

  const handleDeselectAllCampaigns = () => {
    setSelectedCampaignIds([]);
  };

  // Apply search filter to replacements
  const filteredReplacements = filterBySearch(replacements);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="fas fa-user-edit text-primary me-2"></i>
              Attester Replacement Management
            </h2>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary"
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <i className={`fas fa-sync-alt me-2 ${isRefreshing ? 'fa-spin' : ''}`}></i>
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  resetForm();
                  setShowCreateModal(true);
                }}
                disabled={attesters.length === 0}
              >
                <i className="fas fa-plus me-2"></i>
                Create Replacement
              </button>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
              <button type="button" className="btn-close" onClick={() => setError(null)}></button>
            </div>
          )}

          {successMessage && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <i className="fas fa-check-circle me-2"></i>
              {successMessage}
              <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)}></button>
            </div>
          )}

          {/* Alert Banner for Attesters with Account Issues */}
          {attesters.filter(a => a.alert_level === 'critical').length > 0 && (
            <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
              <i className="fas fa-exclamation-triangle fa-lg me-3"></i>
              <div>
                <strong>Immediate Action Required:</strong> {attesters.filter(a => a.alert_level === 'critical').length} attester(s) have disabled accounts and cannot complete their attestations.
                These attestations must be reassigned to ensure campaign completion.
              </div>
            </div>
          )}

          {attesters.filter(a => a.status === 'OUT_OF_OFFICE').length > 0 && (
            <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
              <i className="fas fa-plane fa-lg me-3"></i>
              <div>
                <strong>Out of Office:</strong> {attesters.filter(a => a.status === 'OUT_OF_OFFICE').length} attester(s) have out-of-office auto-replies enabled.
                Consider reassigning their attestations or waiting for their return.
              </div>
            </div>
          )}

          {attesters.filter(a => a.alert_level === 'warning' && a.status !== 'OUT_OF_OFFICE').length > 0 && !attesters.filter(a => a.alert_level === 'critical').length && (
            <div className="alert alert-warning d-flex align-items-center mb-4" role="alert">
              <i className="fas fa-exclamation-circle fa-lg me-3"></i>
              <div>
                <strong>Attention:</strong> {attesters.filter(a => a.alert_level === 'warning' && a.status !== 'OUT_OF_OFFICE').length} attester(s) are marked as inactive in the directory.
                Consider reassigning their attestations.
              </div>
            </div>
          )}

          {/* Summary Stats Cards */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>Active Campaigns</h6>
                      <h2 className="mb-0">{campaigns.length}</h2>
                    </div>
                    <div className="text-muted">
                      <i className="fas fa-flag fa-2x"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>
                        {attesters.filter(a => a.alert_level).length > 0 ? 'Requiring Attention' : 'Attesters with Pending'}
                      </h6>
                      <h2 className={`mb-0 ${attesters.filter(a => a.alert_level).length > 0 ? 'text-danger' : ''}`}>
                        {attesters.filter(a => a.alert_level).length > 0
                          ? attesters.filter(a => a.alert_level).length
                          : attesters.length}
                      </h2>
                    </div>
                    <div className={attesters.filter(a => a.alert_level).length > 0 ? 'text-danger' : 'text-muted'}>
                      <i className={`fas ${attesters.filter(a => a.alert_level).length > 0 ? 'fa-user-slash' : 'fa-users'} fa-2x`}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>Total Pending</h6>
                      <h2 className="mb-0">{attesters.reduce((sum, a) => sum + (a.total_pending || 0), 0)}</h2>
                    </div>
                    <div className="text-muted">
                      <i className="fas fa-clock fa-2x"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>Attester Replaced Records</h6>
                      <h2 className="mb-0">{replacements.length}</h2>
                    </div>
                    <div className="text-muted">
                      <i className="fas fa-exchange-alt fa-2x"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tab strip */}
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                onClick={() => setActiveTab('action')}
                className={`nav-link ${activeTab === 'action' ? 'active' : ''}`}
                style={{
                  fontWeight: activeTab === 'action' ? 'bold' : 'normal',
                  backgroundColor: activeTab === 'action' ? '#4e73df' : 'transparent',
                  color: activeTab === 'action' ? '#ffffff' : '#4e73df',
                  border: activeTab === 'action' ? '2px solid #4e73df' : '2px solid #e3e6f0',
                  borderBottom: activeTab === 'action' ? 'none' : '2px solid #e3e6f0',
                  transition: 'all 0.3s ease',
                  fontSize: '1.05rem',
                  padding: '0.75rem 1.5rem'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'action') {
                    e.currentTarget.style.backgroundColor = '#e8edf7';
                    e.currentTarget.style.color = '#2e59d9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'action') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#4e73df';
                  }
                }}
              >
                <i className="fas fa-user-clock me-2"></i>
                Replace Attester
                {attesters.length > 0 && (
                  <span
                    className="badge ms-2"
                    style={{
                      backgroundColor: activeTab === 'action' ? '#ffffff' : '#4e73df',
                      color: activeTab === 'action' ? '#4e73df' : '#ffffff'
                    }}
                  >
                    {attesters.length}
                  </span>
                )}
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                onClick={() => setActiveTab('history')}
                className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                style={{
                  fontWeight: activeTab === 'history' ? 'bold' : 'normal',
                  backgroundColor: activeTab === 'history' ? '#4e73df' : 'transparent',
                  color: activeTab === 'history' ? '#ffffff' : '#4e73df',
                  border: activeTab === 'history' ? '2px solid #4e73df' : '2px solid #e3e6f0',
                  borderBottom: activeTab === 'history' ? 'none' : '2px solid #e3e6f0',
                  transition: 'all 0.3s ease',
                  fontSize: '1.05rem',
                  padding: '0.75rem 1.5rem'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'history') {
                    e.currentTarget.style.backgroundColor = '#e8edf7';
                    e.currentTarget.style.color = '#2e59d9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'history') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#4e73df';
                  }
                }}
              >
                <i className="fas fa-exchange-alt me-2"></i>
                Replacement History by Campaign
              </button>
            </li>
          </ul>

          {activeTab === 'action' && (
          /* Attesters with Pending Attestations */
          <div className="card mb-4 shadow-sm">
            <div className="card-body p-0">
              {attesters.length === 0 ? (
                <div className="text-center py-5">
                  <div className="text-success mb-3">
                    <i className="fas fa-check-circle fa-4x"></i>
                  </div>
                  <h5 className="text-success mb-2">All Caught Up!</h5>
                  <p className="text-muted mb-0">No attesters have pending attestations across active campaigns.</p>
                  <p className="text-muted"><small>Replacements are only needed when an attester has pending work.</small></p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th style={{ width: '40px' }}></th>
                        <th>Attester</th>
                        <th>Account Status</th>
                        <th className="text-center">OOO</th>
                        <th>OOO Start</th>
                        <th>OOO End</th>
                        <th className="text-center">Pending</th>
                        <th>Campaigns</th>
                        <th className="text-center" style={{ width: '120px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attesters.map((attester, index) => {
                        const hasAlert = attester.alert_level === 'critical' || attester.alert_level === 'warning';
                        const rowBg = attester.alert_level === 'critical' ? 'rgba(220, 53, 69, 0.08)' :
                                     attester.alert_level === 'warning' ? 'rgba(253, 126, 20, 0.08)' : '';

                        return (
                          <tr
                            key={attester.email || index}
                            style={{
                              background: rowBg,
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              handleAttesterSelect(attester);
                              setShowCreateModal(true);
                            }}
                          >
                            {/* Status Indicator */}
                            <td className="align-middle text-center">
                              {attester.alert_level === 'critical' ? (
                                <i className="fas fa-exclamation-circle text-danger" title="Account Disabled"></i>
                              ) : attester.status === 'OUT_OF_OFFICE' ? (
                                <i className="fas fa-plane text-info" title="Out of Office"></i>
                              ) : attester.alert_level === 'warning' ? (
                                <i className="fas fa-exclamation-triangle text-warning" title="Inactive User"></i>
                              ) : (
                                <i className="fas fa-check-circle text-success" title="Active"></i>
                              )}
                            </td>

                            {/* Attester Info */}
                            <td className="align-middle">
                              <div className="d-flex align-items-center">
                                <div style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  background: hasAlert ? (attester.alert_level === 'critical' ? '#dc3545' : '#fd7e14') : '#6c757d',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginRight: '10px',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: '0.9rem'
                                }}>
                                  {(attester.name || 'U').charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div style={{ fontWeight: '500' }}>{attester.name || 'Unknown'}</div>
                                  <small className="text-muted">{attester.email}</small>
                                </div>
                              </div>
                            </td>

                            {/* Account Status */}
                            <td className="align-middle">
                              {attester.status === 'ACCOUNT_DISABLED' ? (
                                <span className="text-danger">
                                  <i className="fas fa-user-slash me-1"></i>
                                  Disabled
                                </span>
                              ) : attester.status === 'INACTIVE' ? (
                                <span className="text-warning">
                                  <i className="fas fa-user-clock me-1"></i>
                                  Inactive
                                </span>
                              ) : (
                                <span className="text-success">
                                  <i className="fas fa-user-check me-1"></i>
                                  Active
                                </span>
                              )}
                            </td>

                            {/* Out of Office - Yes/No */}
                            <td className="align-middle text-center">
                              {attester.is_out_of_office ? (
                                <span className="text-info">
                                  <i className="fas fa-plane me-1"></i>
                                  Yes
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>

                            {/* OOO Start Date */}
                            <td className="align-middle">
                              {attester.ooo_start_date ? (
                                <small>{attester.ooo_start_date.substring(0, 10)}</small>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>

                            {/* OOO End Date */}
                            <td className="align-middle">
                              {attester.ooo_end_date ? (
                                <small>{attester.ooo_end_date.substring(0, 10)}</small>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>

                            {/* Pending Count */}
                            <td className="align-middle text-center">
                              <span className={
                                attester.total_pending >= 50 ? 'text-danger fw-bold' :
                                attester.total_pending >= 20 ? 'text-warning fw-bold' :
                                attester.total_pending >= 10 ? 'text-info' : 'text-muted'
                              }>
                                {attester.total_pending}
                              </span>
                            </td>

                            {/* Campaigns */}
                            <td className="align-middle">
                              <div>
                                <small className="text-muted">{attester.campaigns.length} campaign{attester.campaigns.length !== 1 ? 's' : ''}</small>
                                {attester.campaigns.length <= 3 && (
                                  <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                    {attester.campaigns.map(c => c.campaign_name).join(', ')}
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Action */}
                            <td className="align-middle text-center">
                              <button
                                className={`btn btn-sm ${hasAlert ? 'btn-danger' : 'btn-primary'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAttesterSelect(attester);
                                  setShowCreateModal(true);
                                }}
                              >
                                <i className="fas fa-exchange-alt me-1"></i>
                                Replace
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          )}

          {activeTab === 'history' && (
              /* Attester Replacements By Campaign */
              <div className="card mb-4 shadow-sm">
                <div className="card-body">
                  <div className="row g-2 mb-3">
                    <div className="col-md-6">
                      <select
                        className="form-select"
                        value={selectedCampaignId}
                        onChange={(e) => setSelectedCampaignId(e.target.value)}
                      >
                        <option value="">Select a campaign to view replacements...</option>
                        {campaigns.map(campaign => (
                          <option key={campaign.campaign_id} value={campaign.campaign_id}>
                            {campaign.campaign_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {selectedCampaignId && (
                      <div className="col-md-6">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search replacements..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    )}
                  </div>

                  {!selectedCampaignId ? (
                    campaigns.length > 0 ? (
                      <div className="alert alert-info mb-0">
                        <i className="fas fa-arrow-up me-2"></i>
                        Select a campaign above to view its replacement history
                      </div>
                    ) : null
                  ) : isLoadingReplacements ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : filteredReplacements.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="fas fa-inbox fa-2x mb-2"></i>
                      <p className="mb-0">No attester replacements found for this campaign</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-striped table-hover mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th>Database</th>
                            <th>Username</th>
                            <th>Original Attester</th>
                            <th>Replaced With</th>
                            <th>Reason</th>
                            <th>Replaced By</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredReplacements.map((replacement, index) => (
                            <tr key={replacement.replacement_id || index}>
                              <td><strong>{replacement.database_name}</strong></td>
                              <td><code>{replacement.db_username}</code></td>
                              <td>
                                <small>{replacement.from_attester_email}</small>
                              </td>
                              <td>
                                <strong>{replacement.to_attester_email}</strong>
                              </td>
                              <td>
                                {replacement.reason ? (
                                  <span className="text-info">{replacement.reason}</span>
                                ) : '-'}
                              </td>
                              <td>{replacement.replaced_by || '-'}</td>
                              <td><small>{formatDate(replacement.replacement_date)}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
          )}
        </div>
      </div>

      {/* Create Modal - Attester-First Workflow */}
      {showCreateModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-user-edit me-2"></i>
                  Create Attester Replacement
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateModal(false)}
                ></button>
              </div>
              <form onSubmit={handleCreateReplacement}>
                <div className="modal-body">
                  {/* Errors must render INSIDE the modal — the page-level alert sits
                      behind this fixed overlay and is invisible while the modal is open. */}
                  {error && (
                    <div className="alert alert-danger alert-dismissible mb-3">
                      <i className="fas fa-exclamation-circle me-2"></i>
                      {error}
                      <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                    </div>
                  )}
                  <div className="row">
                    {/* Left Column - Attester Selection */}
                    <div className="col-md-4 border-end">
                      <h6 className="text-primary mb-3">
                        <i className="fas fa-user me-2"></i>
                        Step 1: Select Attester to Replace
                      </h6>

                      <div className="list-group" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {attesters.map((attester, index) => (
                          <button
                            type="button"
                            key={attester.email || index}
                            className={`list-group-item list-group-item-action ${selectedAttester?.email === attester.email ? 'active' : ''}`}
                            onClick={() => handleAttesterSelect(attester)}
                          >
                            <div className="d-flex w-100 justify-content-between">
                              <strong>{attester.name || 'N/A'}</strong>
                              <span className={selectedAttester?.email === attester.email ? 'text-dark' : 'text-warning fw-bold'}>
                                {attester.total_pending}
                              </span>
                            </div>
                            <small className={selectedAttester?.email === attester.email ? 'text-light' : 'text-muted'}>
                              {attester.email}
                            </small>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Middle Column - Campaign & Users Impact */}
                    <div className="col-md-4 border-end">
                      <h6 className="text-primary mb-3">
                        <i className="fas fa-clipboard-list me-2"></i>
                        Step 2: Select Campaigns
                      </h6>

                      {!selectedAttester ? (
                        <div className="text-center text-muted py-4">
                          <i className="fas fa-arrow-left fa-2x mb-2"></i>
                          <p>Select an attester first</p>
                        </div>
                      ) : isLoadingDetails ? (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : attesterDetails ? (
                        <>
                          <div className="d-flex justify-content-between mb-2">
                            <small className="text-muted">
                              {attesterDetails.campaigns.length} campaign(s), {attesterDetails.total_pending} user(s)
                            </small>
                            <div>
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 me-2"
                                onClick={handleSelectAllCampaigns}
                              >
                                Select All
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0"
                                onClick={handleDeselectAllCampaigns}
                              >
                                Deselect All
                              </button>
                            </div>
                          </div>

                          <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                            {attesterDetails.campaigns.map((campaign, idx) => (
                              <div key={campaign.campaign_id} className="card mb-2">
                                <div className="card-header py-2">
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id={`campaign-${campaign.campaign_id}`}
                                      checked={selectedCampaignIds.includes(campaign.campaign_id)}
                                      onChange={() => handleCampaignToggle(campaign.campaign_id)}
                                    />
                                    <label
                                      className="form-check-label d-flex justify-content-between w-100"
                                      htmlFor={`campaign-${campaign.campaign_id}`}
                                    >
                                      <strong>{campaign.campaign_name}</strong>
                                      <span className="text-warning fw-bold">{campaign.pending_count}</span>
                                    </label>
                                  </div>
                                </div>
                                {selectedCampaignIds.includes(campaign.campaign_id) && campaign.users && (
                                  <div className="card-body py-2">
                                    <small className="text-muted">Affected users:</small>
                                    <ul className="list-unstyled mb-0 small">
                                      {campaign.users.slice(0, 5).map((user, uidx) => (
                                        <li key={uidx}>
                                          <code>{user.db_username}</code>
                                          <span className="text-muted"> - {user.database_name}</span>
                                        </li>
                                      ))}
                                      {campaign.users.length > 5 && (
                                        <li className="text-muted">...and {campaign.users.length - 5} more</li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="alert alert-warning">No details available</div>
                      )}
                    </div>

                    {/* Right Column - Replacement Details */}
                    <div className="col-md-4">
                      <h6 className="text-primary mb-3">
                        <i className="fas fa-user-plus me-2"></i>
                        Step 3: Replacement Details
                      </h6>

                      <div className="mb-3">
                        <label className="form-label">Attester Being Replaced</label>
                        <input
                          type="text"
                          className="form-control"
                          value={selectedAttester ? `${selectedAttester.name || 'N/A'} (${selectedAttester.email})` : ''}
                          readOnly
                          disabled
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Replacement Attester *</label>
                        <select
                          className="form-select"
                          value={formData.new_attester_email}
                          onChange={(e) => {
                            // Check suggested replacements first
                            let selectedUser = null;
                            if (suggestedReplacements.manager?.email === e.target.value) {
                              selectedUser = suggestedReplacements.manager;
                            } else {
                              selectedUser = suggestedReplacements.colleagues.find(c => c.email === e.target.value);
                            }
                            // If not found in suggestions, check all users
                            if (!selectedUser) {
                              selectedUser = users.find(u => u.email === e.target.value);
                            }
                            setFormData(prev => ({
                              ...prev,
                              new_attester_email: e.target.value,
                              new_attester_name: selectedUser?.display_name || ''
                            }));
                          }}
                          required
                          disabled={!selectedAttester}
                        >
                          <option value="">Select replacement attester...</option>

                          {/* Suggested Section */}
                          {(suggestedReplacements.manager || suggestedReplacements.colleagues.length > 0) && (
                            <optgroup label="📌 Suggested Replacements">
                              {/* Manager - highest priority */}
                              {suggestedReplacements.manager && suggestedReplacements.manager.email !== formData.old_attester_email && (
                                <option
                                  key={`mgr-${suggestedReplacements.manager.email}`}
                                  value={suggestedReplacements.manager.email}
                                  style={{ fontWeight: 'bold' }}
                                >
                                  ⭐ {suggestedReplacements.manager.display_name || 'N/A'} - Manager ({suggestedReplacements.manager.job_title || 'N/A'})
                                </option>
                              )}
                              {/* Colleagues */}
                              {suggestedReplacements.colleagues
                                .filter(c => c.email !== formData.old_attester_email)
                                .map(colleague => (
                                  <option key={`col-${colleague.email}`} value={colleague.email}>
                                    👥 {colleague.display_name || 'N/A'} - {colleague.job_title || 'N/A'} ({colleague.department || 'N/A'})
                                  </option>
                                ))}
                            </optgroup>
                          )}

                          {/* Others Section */}
                          <optgroup label="All Enterprise Users">
                            {users
                              .filter(u => {
                                if (!u.email || u.email === formData.old_attester_email) return false;
                                // Exclude users already in suggested section
                                if (suggestedReplacements.manager?.email === u.email) return false;
                                if (suggestedReplacements.colleagues.some(c => c.email === u.email)) return false;
                                return true;
                              })
                              .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''))
                              .map(user => (
                                <option key={user.email} value={user.email}>
                                  {user.display_name || 'N/A'} ({user.employee_id || 'N/A'}, {user.job_title || 'N/A'}, {user.department || 'N/A'})
                                </option>
                              ))}
                          </optgroup>
                        </select>
                        {/* Suggestion helper text */}
                        {selectedAttester && (suggestedReplacements.manager || suggestedReplacements.colleagues.length > 0) && (
                          <small className="text-muted">
                            <i className="fas fa-lightbulb text-warning me-1"></i>
                            Suggested replacements shown first based on organizational hierarchy
                          </small>
                        )}
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Reason *</label>
                        <select
                          className="form-select"
                          value={formData.reason}
                          onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                          required
                          disabled={!selectedAttester}
                        >
                          <option value="">Select reason...</option>
                          {reasonCodes.map(code => (
                            <option key={code.value} value={code.value}>{code.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Comments</label>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={formData.comments}
                          onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                          placeholder="Additional comments..."
                          disabled={!selectedAttester}
                        />
                      </div>

                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      !selectedAttester ||
                      !formData.new_attester_email ||
                      !formData.reason ||
                      selectedCampaignIds.length === 0 ||
                      formData.old_attester_email.toLowerCase() === formData.new_attester_email.toLowerCase()
                    }
                  >
                    <i className="fas fa-check me-2"></i>
                    Apply Replacement
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Replacement result modal — shown after a successful Apply Replacement.
          Surfaces the cross-campaign summary ("N accounts updated across M campaigns")
          along with a per-campaign breakdown so admins can confirm the scope of the
          change in one place instead of flipping the campaign dropdown to verify. */}
      <WideModal
        show={!!replacementResult}
        onClose={() => setReplacementResult(null)}
        title="Attester Replacement Applied"
        headerIcon="fa-check-circle"
        headerClass="bg-success text-white"
        maxWidth="900px"
        footer={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setReplacementResult(null)}
          >
            Close
          </button>
        }
      >
        {replacementResult && (
          <div>
            <div className="alert alert-success mb-3">
              <i className="fas fa-check-circle me-2"></i>
              Successfully replaced attester. <strong>{replacementResult.total_records_updated}</strong> account(s) updated across <strong>{(replacementResult.campaigns_updated || []).length}</strong> campaign(s).
            </div>

            {(replacementResult.skipped_self_attestation > 0) && (
              <div className="alert alert-warning mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>{(replacementResult.skipped_self_users || []).join(', ')}</strong> user is skipped as self-attestation is not allowed
              </div>
            )}

            <div className="row mb-3">
              <div className="col-md-6">
                <div className="text-muted small">From</div>
                <div><i className="fas fa-user-slash text-danger me-2"></i>{replacementResult.old_attester}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted small">To</div>
                <div><i className="fas fa-user-check text-success me-2"></i>{replacementResult.new_attester}</div>
              </div>
            </div>

            <h6 className="mb-2">Per-Campaign Breakdown</h6>
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>Campaign</th>
                    <th className="text-end" style={{ width: '180px' }}>Attester Replaced Records</th>
                  </tr>
                </thead>
                <tbody>
                  {(replacementResult.campaigns_updated || []).map(c => (
                    <tr key={c.campaign_id}>
                      <td>{c.campaign_name}</td>
                      <td className="text-end">{c.records_updated}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </WideModal>
    </div>
  );
};

export default AttesterReplacement;
